import {
  BoxBoundary,
  Area,
  ComputedData,
  PackedRect,
  Solution,
  SolutionRect,
  Currency,
  Foil,
} from "./Models"
import { algorithms } from "../algorithms/AlgoDict"
import { mm2tom2 } from "./Conversion"

export function solveAndFindBest(
  docWidth: number,
  docHeight: number,
  spacing: number,
  docBorder: number,
  origRects: Array<PackedRect>,
  pricePerM2: number,
  selectedAlgos: Array<string>,
  currency: Currency,
  rate: number,
  foil: Foil,
  marginDivisor: number,
  machineGripMM: number,
  plotrPrice: [number, number, number],
  plotrBreaks: [number, number, number, number]
): Solution | string {
  const tooBig = tooBigRect(origRects, docWidth - 2 * docBorder - 2 * spacing)
  if (tooBig) return `Your cut-out ${tooBig} can't fit on foil width.`

  if (selectedAlgos.length > 0 && origRects.length > 0) {
    let minArea = Number.MAX_VALUE
    let best: Solution | undefined = undefined

    for (const algo of selectedAlgos) {
      console.log("Running algorithm: " + algo)
      let result = undefined
      try {
        result = solve(
          docWidth,
          docHeight,
          spacing,
          docBorder,
          origRects,
          pricePerM2,
          algo,
          currency,
          rate,
          foil,
          marginDivisor,
          machineGripMM,
          plotrPrice,
          plotrBreaks
        )
      } catch (error) {
        if (error instanceof Error) {
          console.log(error.message.split(";")[1])
          if (error.message.split(";")[1] === "dimensions") {
            return `Your cut-out ${
              error.message.split(";")[0]
            } is out of range (${plotrBreaks[0]}"–${plotrBreaks[3]}").`
          }
        }
      }

      console.log(" - Result: ", result)
      if (result === undefined) {
        console.log("Skipping ", algo)
        continue
      }
      const used = result.computedData.totalAreaMM2
      if (used < minArea && checkClipping(result.computedData.rects)) {
        minArea = used
        best = result
        best.bestAlgo = algo
      }
    }
    if (best === undefined)
      return "There was a problem while trying to arange the objects."
    return best
  } else {
    const solution = solve(
      docWidth,
      docHeight,
      spacing,
      docBorder,
      [],
      pricePerM2,
      "Empty",
      currency,
      rate,
      foil,
      marginDivisor,
      machineGripMM,
      plotrPrice,
      plotrBreaks
    )
    if (solution) return solution
    return "No solution found"
  }
}

export function solve(
  docWidth: number,
  docHeight: number,
  spacing: number,
  docBorder: number,
  origRects: Array<PackedRect>,
  pricePerM2: number,
  selectedAlgo: string,
  currency: Currency,
  rate: number,
  foil: Foil,
  marginDivisor: number,
  machineGripMM: number,
  plotrPrice: [number, number, number],
  plotrBreaks: [number, number, number, number]
): Solution | undefined {
  //renaming duplicit
  for (var i = 0; i < origRects.length; i++) {
    for (var j = 0; j < origRects.length; j++) {
      if (i === j) continue
      if (origRects[i].id === origRects[j].id)
        origRects[j].id = origRects[j].id + "-" + j.toString()
    }
  }

  const foundAlgo = algorithms.find((a) => a.name === selectedAlgo)
  if (foundAlgo === undefined) {
    throw Error(`Selcted algo ${selectedAlgo} is not in algoDict`)
  }

  const workingAreaW = docWidth - 2 * docBorder - spacing
  const workingAreaH = docHeight
  const workingPacked: Array<SolutionRect> = []
  origRects.forEach((rect) => {
    for (let i = 0; i < rect.cnt; i++) {
      workingPacked.push({
        x: 0,
        y: 0,
        r: rect.r,
        w: rect.w + spacing,
        h: rect.h + spacing,
        id: rect.id + `- ${i}`,
        pairId: rect.id,
      })
    }
  })
  workingPacked.sort(
    (a, b) => (a.w > b.w && -1) || (a.w < b.w && 1) || (a.h > b.h && -1) || 1
  )

  const cdata = foundAlgo.func(
    { w: workingAreaW, h: workingAreaH },
    workingPacked
  )
  if (cdata === null) return undefined
  //After algo, radiuses are fucked somehow
  cdata.forEach((rect) => {
    const o_rect_match = origRects.find((oRect) => oRect.id === rect.pairId)
    if (o_rect_match === undefined) {
      console.log(rect, origRects, cdata)
      throw new Error("Wtf, rect from cdata is not in origRects")
    }
    rect.r = o_rect_match.r
  })

  cdata.forEach((rect) => {
    rect.w -= spacing
    rect.h -= spacing
    rect.x += docBorder
    rect.y += docBorder
    rect.x += spacing
    rect.y += spacing
  })

  const bbox = findBoundingBox(cdata)
  bbox.x -= spacing + docBorder
  bbox.y -= spacing + docBorder
  bbox.w += 2 * spacing + 2 * docBorder
  bbox.h += 2 * spacing + 2 * docBorder

  let allbox = {
    w: docWidth,
    h: bbox.h,
    x: 0,
    y: 0,
  }

  // Let's always go from top to bottom instead:
  const compData = getComputedData({
    allBox: allbox,
    boundingBox: bbox,
    spacing: spacing,
    pricePerM2: pricePerM2,
    unpricedSolutionRects: cdata,
    docBorder: docBorder,
    currency: currency,
    rate: rate,
    foil: foil,
    machineGripMM: machineGripMM,
    marginDivisor,
    plotrPrice,
    plotrBreaks
  })

  if (
    (compData.boundingBox.h > docHeight || compData.boundingBox.w > docWidth) &&
    selectedAlgo !== "Empty"
  )
    return undefined

  return {
    computedData: compData,
    boundingBox: bbox,
    allBox: allbox,
    bestAlgo: "",
  }
}

export function checkClipping(rectangles: Array<SolutionRect>): boolean {
  for (let i = 0; i < rectangles.length; i++) {
    for (let j = i + 1; j < rectangles.length; j++) {
      if (rectanglesOverlap(rectangles[i], rectangles[j])) {
        return false
      }
    }
  }
  return true
}

export function rectanglesOverlap(
  rect1: SolutionRect,
  rect2: SolutionRect
): boolean {
  return !(
    rect1.x + rect1.w <= rect2.x || // rect1 is to the left of rect2
    rect2.x + rect2.w <= rect1.x || // rect2 is to the left of rect1
    rect1.y + rect1.h <= rect2.y || // rect1 is above rect2
    rect2.y + rect2.h <= rect1.y
  ) // rect2 is above rect1
}

/* export function manualSolve(
  docWidth: number,
  docHeight: number,
  spacing: number,
  docBorder: number,
  data: Array<SolutionRect>,
  pricePerM2: number,
  currency: Currency,
  rate: number,
  foil: Foil
): {
  allArea: Area
  computedData: ComputedData
  boundingBox: BoxBoundary
  allBox: BoxBoundary
} {
  let area = {
    w: docWidth - spacing,
    h: docHeight - spacing,
  }

  const bbox = findBoundingBox(data)
  bbox.w += docBorder * 2
  bbox.h += docBorder * 2

  const allbox: BoxBoundary = {
    w: area.w,
    h: bbox.h,
    x: 0,
    y: bbox.y,
  }

  const compData = getComputedData({
    allBox: allbox,
    boundingBox: bbox,
    spacing: spacing,
    pricePerM2: pricePerM2,
    unpricedSolutionRects: data,
    docBorder: docBorder,
    currency: currency,
    rate: rate,
    foil: foil,
  })

  return {
    allArea: { w: area.w + spacing, h: area.h + spacing },
    computedData: compData,
    boundingBox: bbox,
    allBox: allbox,
  }
} */

export function getComputedData(props: {
  allBox: BoxBoundary
  boundingBox: BoxBoundary
  spacing: number
  pricePerM2: number
  unpricedSolutionRects: Array<SolutionRect>
  docBorder: number
  currency: Currency
  rate: number
  foil: Foil
  marginDivisor: number
  machineGripMM: number
  plotrPrice: [number, number, number],
  plotrBreaks: [number, number, number, number]
}): ComputedData {
  const data = props.unpricedSolutionRects
  const aw = props.allBox.w
  const ah = props.allBox.h
  const totalAreaMM2 = aw * ah
  const machineMarginDiscountMultiplier = (aw - 2 * props.machineGripMM) / aw

  const plotrPrice = plotrPriceSum(
    data.map((rect) => {
      return { w: rect.w, h: rect.h, id: rect.pairId }
    }),
    props.currency,
    props.rate,
    props.foil,
    props.plotrPrice,
    props.plotrBreaks
  )
  const plotrPricePerM = plotrPrice / mm2tom2(totalAreaMM2)

  const totalPricePerM =
    (props.pricePerM2 + plotrPricePerM) / props.marginDivisor * machineMarginDiscountMultiplier

  const usedAreaMM2 = data.reduce(
    (prev, current) => (prev += current.w * current.h),
    0
  )

  const moneyWasted = mm2tom2(totalAreaMM2 - usedAreaMM2) * props.pricePerM2
  const priceStats: {
    [key: string]: { one: number; all: number; cnt: number }
  } = {}
  const rects: Array<SolutionRect> = []
  data.forEach((data_: SolutionRect) => {
    let x = data_.x
    let y = data_.y
    let w = data_.w
    let h = data_.h
    let r = data_.r

    if (!priceStats[data_.pairId]) {
      let size = w * h
      let p = (size / usedAreaMM2) * mm2tom2(totalAreaMM2) * totalPricePerM
      priceStats[data_.pairId] = { one: p, all: 0, cnt: 0 }
    }
    priceStats[data_.pairId].all += priceStats[data_.pairId].one
    priceStats[data_.pairId].cnt += 1

    rects.push({
      x: x,
      y: y,
      w: w,
      h: h,
      r: r,
      id: data_.id,
      pairId: data_.pairId,
      pricePerRect: Math.round(priceStats[data_.pairId].one),
    })
  })
  return {
    allBox: props.allBox,
    boundingBox: props.boundingBox,
    spacing: props.spacing,
    pricePerM2: totalPricePerM,
    rects: data,
    solvedRects: rects,
    totalAreaMM2: totalAreaMM2,
    usedAreaMM2: usedAreaMM2,
    wasted: moneyWasted,
    priceStats: priceStats,
  }
}

export function findBoundingBox(rectangles: Array<SolutionRect>): BoxBoundary {
  if (rectangles.length === 0) return { x: 0, y: 0, w: 0, h: 0 }

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  rectangles.forEach((rect) => {
    minX = Math.min(minX, rect.x)
    minY = Math.min(minY, rect.y)
    maxX = Math.max(maxX, rect.x + rect.w)
    maxY = Math.max(maxY, rect.y + rect.h)
  })
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
}

function plotrPriceSum(
  rects: Array<Area & { id: string }>,
  currency: Currency,
  rate: number,
  foil: Foil,
  plotrPriceArr: [number, number, number],
  plotrBreaks: [number, number, number, number]
): number {
  if (foil !== "LOTUS Foil") return 0
  const a = rects.reduce(
    (sum: number, rect: Area & { id: string }) =>
      sum + plotrPrice(rect, currency, rate, plotrPriceArr, plotrBreaks),
    0
  )
  return a
}

function plotrPrice(
  rect: Area & { id: string },
  currency: Currency,
  rate: number,
  plotrPriceArr: [number, number, number],
  plotrBreaks: [number, number, number, number]
): number {
  /* TODO admin can change price per plotr */
  const diagonal = rectToInchRound(rect, plotrBreaks)
  if (!diagonal) throw new Error(`${rect.id};dimensions`)
  if (diagonal === 14)
    return currency === "CZK" ? plotrPriceArr[0] : plotrPriceArr[0] / rate
  if (diagonal === 22)
    return currency === "CZK" ? plotrPriceArr[1] : plotrPriceArr[1] / rate
  return currency === "CZK" ? plotrPriceArr[2] : plotrPriceArr[2] / rate
}

function rectToInchRound(rect: Area, plotrBreaks: [number, number, number, number]): number | undefined {
  const diagonalINCH = rectToInch(rect)

  if (diagonalINCH < plotrBreaks[0]) {
    return undefined
  }
  if (diagonalINCH <= plotrBreaks[1]) {
    return 14
  }
  if (diagonalINCH <= plotrBreaks[2]) {
    return 22
  }
  if (diagonalINCH <= plotrBreaks[3]) {
    return 32
  }
  return undefined
}

export function rectToInch(rect: Area): number {
  const diagonalMM = Math.sqrt(rect.h ** 2 + rect.w ** 2)
  return diagonalMM / 25.4
}

function tooBigRect(
  rects: Array<Area & { id: string }>,
  docWidth: number
): string | undefined {
  for (const rect of rects) {
    if (rect.h > docWidth && rect.w > docWidth) {
      return rect.id
    }
  }
  return undefined
}
